import { useMemo } from 'react'
import {
  getProgressPercentage,
  projectRouteToSvg,
  sampleRoutePoints,
  type RouteCoordinate,
} from '../../logic/routeUtils'
import styles from './RoutePreview.module.css'

interface RoutePreviewProps {
  routeCoordinates: RouteCoordinate[]
  progress: number
  totalDistance: number
  height?: number
}

export default function RoutePreview({
  routeCoordinates,
  progress,
  totalDistance,
  height = 150,
}: RoutePreviewProps) {
  const width = 320

  const { completedPath, remainingPath, marker } = useMemo(() => {
    const samples = sampleRoutePoints(routeCoordinates)
    const points = projectRouteToSvg(samples, width, height)
    if (points.length < 2) {
      return { completedPath: '', remainingPath: '', marker: null as { x: number; y: number } | null }
    }

    const progressPercent = getProgressPercentage(progress, totalDistance)
    const splitIndex = Math.round((progressPercent / 100) * (points.length - 1))
    const clampedIndex = Math.max(0, Math.min(splitIndex, points.length - 1))

    const toPath = (slice: { x: number; y: number }[]) =>
      slice.map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ')

    return {
      completedPath: toPath(points.slice(0, clampedIndex + 1)),
      remainingPath: toPath(points.slice(clampedIndex)),
      marker: points[clampedIndex] ?? null,
    }
  }, [routeCoordinates, progress, totalDistance, height])

  if (!completedPath && !remainingPath) {
    return (
      <div className={styles.root} style={{ height }}>
        <span className={styles.empty}>Route unavailable</span>
      </div>
    )
  }

  return (
    <div className={styles.root} style={{ height }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={styles.svg}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
      >
        {remainingPath && (
          <path
            d={remainingPath}
            fill="none"
            stroke="var(--muted-2)"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        )}
        {completedPath && (
          <path
            d={completedPath}
            fill="none"
            stroke="var(--green)"
            strokeWidth={3.5}
            strokeLinecap="round"
          />
        )}
        {marker && (
          <circle
            cx={marker.x}
            cy={marker.y}
            r={5}
            fill="var(--green)"
            stroke="var(--surface)"
            strokeWidth={2}
          />
        )}
      </svg>
    </div>
  )
}
